"use client";

import { memo, useCallback, useId, useState } from "react";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { PlaceChip, PlaceDidYouMean } from "@/components/PlaceChip";
import type { PlaceResult } from "@/lib/api/misc";
import { hasDevanagari } from "@/lib/place-devanagari";
import { kundaliRegionalLocale } from "@/i18n/regional-locales";
import type { KundaliLocale, KundaliTranslations } from "./KundaliFormCard.i18n";
import {
  REGIONAL_KUNDALI_FORM_COPY,
  type RegionalKundaliFormCopy,
} from "./KundaliFormCard.i18n.regional";
import {
  birthDateInlineError,
  formatBirthDateDisplayInput,
  normalizeBirthDateInput,
} from "./KundaliFormCard.validation";
import { KUNDALI_NAME_CWV_PROBE } from "./KundaliFormCard.cwv";

interface FieldWrapperProps {
  children: React.ReactNode;
}

export function FieldWrapper({ children }: FieldWrapperProps) {
  return <div>{children}</div>;
}

type RegionalHint = keyof RegionalKundaliFormCopy["hints"];

interface FieldHintProps {
  id?: string;
  language: KundaliLocale;
  en: string;
  hi: string;
  /** The regional editions' line for this hint. */
  regional: RegionalHint;
}

/** `en`/`hi` as given, or the in-language edition's copy for `key`. */
function hintText(language: KundaliLocale, key: RegionalHint, en: string, hi: string): string {
  const regional = kundaliRegionalLocale(language);
  if (regional) return REGIONAL_KUNDALI_FORM_COPY[regional].hints[key];
  return language === "hi" ? hi : en;
}

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="mt-2 text-caption font-medium text-intent-error">
      {message}
    </p>
  );
}

function FieldHint({ id, language, en, hi, regional }: FieldHintProps) {
  return (
    <p id={id} className="mb-2 text-caption text-text-muted">
      {hintText(language, regional, en, hi)}
    </p>
  );
}

// --- NameField ---

interface NameFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLocale;
  t: KundaliTranslations;
  error?: string;
}

export const NameField = memo(function NameField({
  value,
  onChange,
  onFocus,
  language,
  t,
  error,
}: NameFieldProps) {
  return (
    <FieldWrapper>
      <label htmlFor="name" className="mb-1 block text-body-small font-semibold text-text-primary">
        {t.name}
      </label>
      {/* hi hints are Devanagari Hindi, like the rest of the /hi/ form (owner
          decision 2026-10-28, replacing the 2026-10-08 Hinglish rule). */}
      <FieldHint
        language={language}
        id="name-hint"
        regional="name"
        hi="आपका नाम फल को आपके लिए निजी बनाता है"
        en="Your name helps our AI Jyotishi personalize your reading"
      />
      <input
        id="name"
        data-cwv-interaction-target={KUNDALI_NAME_CWV_PROBE.marker}
        type="text"
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        placeholder={t.namePlaceholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "name-hint name-error" : "name-hint"}
        className="input-sacred min-h-11 w-full rounded-lg px-4 py-3 text-body text-text-primary"
      />
      <FieldError id="name-error" message={error} />
    </FieldWrapper>
  );
});

// --- BirthDateField ---

interface BirthDateFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLocale;
  t: KundaliTranslations;
  error?: string;
}

export const BirthDateField = memo(function BirthDateField({
  value,
  onChange,
  onFocus,
  language,
  t,
  error,
}: BirthDateFieldProps) {
  // Inline as-typed bound check (rejects pre-1900 / future / impossible dates the
  // moment a complete date is typed). The submit-time `error` still wins when
  // present; otherwise the inline message guides the seeker before they submit
  // — this is the lever against the 8,684 birthDate failures.
  const inlineError = birthDateInlineError(value, language);
  const shownError = error ?? inlineError ?? undefined;
  const hintId = "birthDate-hint";
  const errorId = "birthDate-error";
  const describedBy = [hintId, shownError ? errorId : null].filter(Boolean).join(" ");
  function handleBlur() {
    const iso = normalizeBirthDateInput(value);
    if (iso) onChange(formatBirthDateDisplayInput(iso));
  }
  return (
    <FieldWrapper>
      <label
        htmlFor="birthDate"
        className="mb-1 block text-body-small font-semibold text-text-primary"
      >
        {t.birthDate}
      </label>
      <FieldHint
        language={language}
        regional="birthDate"
        hi="सही जन्म तिथि से आपके ग्रह सही पढ़े जाते हैं"
        en="The exact date reveals the true alignment of the stars"
      />
      <input
        id="birthDate"
        type="text"
        inputMode="numeric"
        pattern="[0-9/.-]*"
        required
        value={value}
        onChange={(e) => onChange(formatBirthDateDisplayInput(e.target.value))}
        onFocus={onFocus}
        onBlur={handleBlur}
        placeholder="dd/mm/yyyy"
        maxLength={10}
        autoComplete="bday"
        autoCorrect="off"
        enterKeyHint="next"
        aria-invalid={Boolean(shownError)}
        aria-describedby={describedBy}
        className="input-sacred min-h-11 w-full rounded-lg px-4 py-3 text-body text-text-primary"
      />
      <p id={hintId} className="mt-2 text-caption text-text-muted">
        {hintText(
          language,
          "dateFormat",
          "Use dd/mm/yyyy, e.g. 15/08/1992.",
          "प्रारूप dd/mm/yyyy, जैसे 15/08/1992।"
        )}
      </p>
      <FieldError id={errorId} message={shownError} />
    </FieldWrapper>
  );
});

// --- BirthTimeField ---

interface BirthTimeFieldProps {
  value: string;
  onChange: (v: string) => void;
  onFocus: () => void;
  language: KundaliLocale;
  t: KundaliTranslations;
  required?: boolean;
  error?: string;
}

export const BirthTimeField = memo(function BirthTimeField({
  value,
  onChange,
  onFocus,
  language,
  t,
  required = false,
  error,
}: BirthTimeFieldProps) {
  return (
    <FieldWrapper>
      <label
        htmlFor="birthTime"
        className="mb-1 block text-body-small font-semibold text-text-primary"
      >
        {t.birthTime}{" "}
        {!required && <span className="font-normal text-text-muted">{t.optional}</span>}
      </label>
      <FieldHint
        language={language}
        id="birthTime-hint"
        regional="birthTime"
        hi="जन्म समय से आपका लग्न और दशा सही पढ़े जाते हैं"
        en="Your birth time fixes your Lagna and dasha timeline"
      />
      <input
        id="birthTime"
        type="time"
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? "birthTime-hint birthTime-error" : "birthTime-hint"}
        className="input-sacred min-h-11 w-full rounded-lg px-4 py-3 text-body text-text-primary"
      />
      <FieldError id="birthTime-error" message={error} />
      {!required && (
        <BirthTimeUnknownHelper toggle={t.timeUnknownToggle} explainer={t.timeUnknownExplainer} />
      )}
    </FieldWrapper>
  );
});

function BirthTimeUnknownHelper({ toggle, explainer }: { toggle: string; explainer: string }) {
  // Inline honesty disclosure (rule 32 dead-click + rule 22 voice): a timeless
  // entry gets only a reduced, time-insensitive reading; it never promises
  // Lagna, houses, divisional charts, or dasha timing the chart cannot support.
  const [open, setOpen] = useState(false);
  const explanationId = useId();
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={open ? explanationId : undefined}
        className="min-h-11 py-2 text-left text-caption font-semibold text-intent-brand underline decoration-dotted underline-offset-4"
      >
        {toggle}
      </button>
      {open && (
        <p
          id={explanationId}
          className="mt-2 rounded-lg bg-surface-raised p-3 text-body-small text-text-secondary"
        >
          {explainer}
        </p>
      )}
    </div>
  );
}

// --- BirthPlaceField ---

interface BirthPlaceFieldProps {
  value: string;
  onChange: (v: string) => void;
  onSelect: (place: PlaceResult) => void;
  onFocus: () => void;
  language: KundaliLocale;
  t: KundaliTranslations;
  error?: string;
  /** The committed city (tapped OR confidently resolved). Chip shows when set. */
  selectedPlace: PlaceResult | null;
  /** Clear the committed city and re-open the search (the chip's "change"). */
  onClearPlace: () => void;
  /** "Did you mean …?" suggestion for an ambiguous typed city, or null. */
  ambiguousSuggestion: PlaceResult | null;
}

export const BirthPlaceField = memo(function BirthPlaceField({
  value,
  onChange,
  onSelect,
  onFocus,
  language,
  t,
  error,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
}: BirthPlaceFieldProps) {
  const hintId = "birthPlace-hint";
  const errorId = "birthPlace-error";
  const describedBy = [hintId, error ? errorId : null].filter(Boolean).join(" ");
  const [noMatch, setNoMatch] = useState(false);
  // Set on the first focus: only then can the no-match line appear, and only
  // then is the invisible reserve below added (the server HTML keeps one line).
  const [opened, setOpened] = useState(false);
  const handleFocus = useCallback(() => {
    setOpened(true);
    onFocus();
  }, [onFocus]);
  return (
    <FieldWrapper>
      <label
        htmlFor="birthPlace"
        className="mb-1 block text-body-small font-semibold text-text-primary"
      >
        {t.birthPlace}
      </label>
      {!selectedPlace && (
        <FieldHint
          language={language}
          regional="birthPlace"
          hi="शहर से आपके चार्ट के सटीक निर्देशांक और समय क्षेत्र मिलते हैं"
          en="Location gives us the exact coordinates and timezone for your chart"
        />
      )}
      {selectedPlace ? (
        // Committed city → unmistakable chip with one-tap correction. The
        // autocomplete input is hidden; the chip IS the placeSelected=true state.
        <div>
          <PlaceChip place={selectedPlace} onChange={onClearPlace} changeLabel={t.placeChange} />
          <PlaceCalculationDetails place={selectedPlace} language={language} />
        </div>
      ) : (
        <div onFocus={handleFocus}>
          <PlaceAutocomplete
            id="birthPlace"
            value={value}
            onChange={onChange}
            onSelect={onSelect}
            placeholder={t.placePlaceholder}
            invalid={Boolean(error)}
            describedBy={describedBy}
            onNoMatchChange={setNoMatch}
          />
          {/* Type-and-submit affordance: typed cities (English, or the big
              cities' Hindi names via the transliteration table) auto-lock on
              submit, so the helper must not imply a dropdown tap is required.
              While a typed town finds nothing, the same line gives the
              correction path instead. The variants wrap to different line
              counts (hi at 360 px: 4 vs 3), so once the field is opened every
              variant sits invisibly in the same grid cell and the swap never
              changes its height (rule 58). The live line is the only one
              announced. The hi hints are Devanagari (owner decision
              2026-10-28). */}
          <div className="mt-2 grid text-caption text-text-muted">
            {opened &&
              placeHintVariants(language).map((variant) => (
                <span
                  key={variant}
                  aria-hidden="true"
                  className="invisible col-start-1 row-start-1"
                >
                  {variant}
                </span>
              ))}
            <p id={hintId} aria-live="polite" className="col-start-1 row-start-1">
              {placeHint(language, noMatch ? value : null)}
            </p>
          </div>
        </div>
      )}
      {!selectedPlace && ambiguousSuggestion && (
        <PlaceDidYouMean
          suggestion={ambiguousSuggestion}
          onAccept={onSelect}
          prefixLabel={t.placeDidYouMean}
        />
      )}
      <FieldError id={errorId} message={error} />
    </FieldWrapper>
  );
});

/** Every line the place hint can show, for the height reserve. */
function placeHintVariants(language: KundaliLocale): string[] {
  // Deduplicated: the regional editions use one no-match line for both scripts.
  const variants = [placeHint(language, null), placeHint(language, "a"), placeHint(language, "क")];
  return [...new Set(variants)];
}

/** The place hint, or (for a typed town that matched nothing) its correction path. */
function placeHint(language: KundaliLocale, noMatchQuery: string | null): string {
  const regional = kundaliRegionalLocale(language);
  if (regional) {
    // The gazetteer resolves Latin spellings (and a few Devanagari city names),
    // so every regional edition asks for the English spelling either way.
    const hints = REGIONAL_KUNDALI_FORM_COPY[regional].hints;
    return noMatchQuery === null ? hints.placeDefault : hints.placeNoMatch;
  }
  const hi = language === "hi";
  if (noMatchQuery !== null && hasDevanagari(noMatchQuery)) {
    return hi
      ? "हिंदी वर्तनी नहीं मिली — शहर का नाम अंग्रेज़ी में लिखें, जैसे Sitapur।"
      : "That Hindi spelling isn't in our list — type the town in English, e.g. Sitapur.";
  }
  if (noMatchQuery !== null) {
    return hi
      ? "इस वर्तनी से कोई शहर नहीं मिला — अपना ज़िला या पास का बड़ा शहर अंग्रेज़ी में लिखें।"
      : "No town found with that spelling — try your district or the nearest bigger town, in English.";
  }
  return hi
    ? "शहर का नाम लिखकर सीधे आगे बढ़ें — सही नाम अपने आप चुन लिया जाएगा। बड़े शहरों के हिंदी नाम (जैसे मुंबई) भी चलेंगे।"
    : "Type your city and submit — exact names lock automatically. You can also tap a suggestion.";
}

const METHOD_VALUE = "Lahiri ayanamsa · Whole-sign houses";

/** @internal */
export function PlaceCalculationDetails({
  place,
  language,
}: {
  place: PlaceResult;
  language: KundaliLocale;
}) {
  const coordinates = `${place.latitude.toFixed(4)}°, ${place.longitude.toFixed(4)}°`;
  const regional = kundaliRegionalLocale(language);
  const labels = regional
    ? REGIONAL_KUNDALI_FORM_COPY[regional].calculation
    : language === "hi"
      ? {
          summary: "Ganana details",
          coordinates: "Coordinates",
          timezone: "Timezone",
          method: "Kundali method",
          methodValue: METHOD_VALUE,
        }
      : {
          summary: "Calculation details",
          coordinates: "Coordinates",
          timezone: "Timezone",
          method: "Chart method",
          methodValue: METHOD_VALUE,
        };

  return (
    <details
      className="mt-2 rounded-lg border border-border-default px-3 py-2 text-caption"
      data-calculation-details
    >
      <summary className="min-h-8 cursor-pointer py-1 font-semibold text-text-muted">
        {labels.summary}
      </summary>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 pb-1 text-text-muted">
        <dt>{labels.coordinates}</dt>
        <dd className="text-right text-text-primary">{coordinates}</dd>
        <dt>{labels.timezone}</dt>
        <dd className="text-right text-text-primary">{place.timezone}</dd>
        <dt>{labels.method}</dt>
        <dd className="text-right text-text-primary">{labels.methodValue}</dd>
      </dl>
    </details>
  );
}
